import CommonMixin from '@/core/pages/CommonMixin';
import { Booking, CreateBooking } from '@/models/bookings';
import * as TelliqService from '@/services/telliq.service';
import { GETTERS } from '@/store/getters';
import {
  CREATE_BOOKING_DATE_FORMAT,
  EXTENDED_TELLIQ_DATE_FORMAT,
  MIN_TIME_FRAME_IN_MINUTES,
  TELLIQ_DATE_FORMAT,
} from '@/utils/constants';
import moment from 'moment';

const isSameStartDate = (date: string, booking: Booking) =>
  moment(date).utcOffset(0, true).diff(booking.startDate, 'days') === 0;
const isSameEndDate = (date: string, booking: Booking) =>
  moment(date).utcOffset(0, true).diff(booking.endDate, 'days') === 0;

export default CommonMixin.extend({
  data() {
    return {
      loading_bookings: false,
      calendar_bookings: new Map<string, Booking[]>(),
      allowed_dates: [] as string[],
      found_end_date_booking: false,
    };
  },

  computed: {
    allowedDates(): (val: string) => boolean {
      return (val: string) => this.allowed_dates.includes(moment(val).utcOffset(0, true).startOf('day').toISOString());
    },
  },

  methods: {
    async getBeforeAndAfterBookings(
      booking: Booking,
      machine_ids: number[],
    ): Promise<{ previous: Booking[]; next: Booking[] }> {
      const previous_bookings = TelliqService.getBookings(
        moment(booking.startDate, TELLIQ_DATE_FORMAT).utcOffset(0, true).subtract(7, 'days').toISOString(),
        moment(booking.startDate, EXTENDED_TELLIQ_DATE_FORMAT).utcOffset(0, true).subtract(1, 'second').toISOString(),
        machine_ids,
      );
      const next_bookings = TelliqService.getBookings(
        moment(booking.endDate, EXTENDED_TELLIQ_DATE_FORMAT).utcOffset(0, true).add(1, 'second').toISOString(),
        moment(booking.endDate, EXTENDED_TELLIQ_DATE_FORMAT)
          .utcOffset(0, true)
          .endOf('day')
          .add(7, 'days')
          .toISOString(),
        machine_ids,
      );
      const bookings = await Promise.all([previous_bookings, next_bookings]);
      return { previous: this.sortBookingsByEndDate(bookings[0]), next: this.sortBookingsByStartDate(bookings[1]) };
    },

    sortBookingsByStartDate(bookings: Booking[]): Booking[] {
      return bookings.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
    },

    sortBookingsByEndDate(bookings: Booking[]): Booking[] {
      return bookings.sort((a, b) => new Date(a.endDate).getTime() - new Date(b.endDate).getTime());
    },

    getMonthlyBookings(period: string): Booking[] {
      return this.calendar_bookings.get(period) ?? [];
    },

    async periodChanged(period: string, from_date: boolean): Promise<void> {
      const edit_booking = this.$store.state.editBooking as string;
      if (edit_booking) {
        if (!this.calendar_bookings.has(period)) {
          this.loading_bookings = true;
          const start_date = moment(period, 'YYYY-MM').utcOffset(0, true).startOf('month').toISOString();
          const end_date = moment(period, 'YYYY-MM').utcOffset(0, true).endOf('month').toISOString();
          const monthly_bookings = await this.fetchBookings(start_date, end_date);
          this.calendar_bookings.set(period, monthly_bookings);
          this.loading_bookings = false;
        }

        const monthly_bookings = this.calendar_bookings.get(period);
        if (monthly_bookings) {
          await this.$nextTick();
          if (from_date) {
            this.computeFromDateAllowedDates(period, monthly_bookings);
          } else {
            this.computeEndDateAllowedDates(period, monthly_bookings);
          }

          this.setDatePickerColor(period, monthly_bookings);
        }
      } else {
        this.setAllDatesAllowed(period);
      }

      const booking = this.$store.state.createBooking as CreateBooking;
      // While editing a booking there might be a chance the start date is set to be after the end date.
      // In that case the start date is ser to be the end date.
      const end_date = moment(booking.endDate, CREATE_BOOKING_DATE_FORMAT).isAfter(
        moment(booking.startDate, CREATE_BOOKING_DATE_FORMAT),
      )
        ? booking.endDate
        : booking.startDate ?? '';
      this.setDatePickerBookingDatesColor(period, booking.startDate ?? '', end_date ?? '');
    },

    async fetchBookings(start_date: string, end_date: string): Promise<Booking[]> {
      try {
        const booking: CreateBooking = this.$store.getters[GETTERS.GET_CREATE_BOOKING];
        return await TelliqService.getBookings(
          start_date,
          end_date,
          booking.machine ? [booking.machine.id] : undefined,
        );
      } catch (e) {
        console.error(e);
      }
      return [];
    },

    setAllDatesAllowed(period: string): void {
      const first_day = moment(period, 'YYYY-MM').utcOffset(0, true).startOf('month').date();
      const last_day = moment(period, 'YYYY-MM').utcOffset(0, true).endOf('month').date();
      this.allowed_dates = [];
      for (let date = first_day; date <= last_day; date++) {
        const current_date = moment(period, 'YYYY-MM').utcOffset(0, true).set('date', date).startOf('day');
        this.allowed_dates.push(current_date.toISOString());
      }
    },

    isDayFullyBooked(date: string, date_bookings: Booking[]): boolean {
      let current_time = moment(date).utcOffset(0, true).startOf('day');
      for (const booking of date_bookings) {
        if (isSameStartDate(current_time.toISOString(), booking)) {
          if (moment(booking.startDate).utcOffset(0, true).diff(current_time, 'minutes') < MIN_TIME_FRAME_IN_MINUTES) {
            current_time = moment(booking.endDate);
          } else {
            return false;
          }
        } else if (isSameEndDate(current_time.toISOString(), booking)) {
          current_time = moment(booking.endDate);
        } else {
          // Between start and end date.
          return true;
        }
      }
      return moment(date).utcOffset(0, true).endOf('day').diff(current_time, 'minutes') < MIN_TIME_FRAME_IN_MINUTES;
    },

    computeFromDateAllowedDates(period: string, bookings: Booking[]): void {
      const first_day = moment(period, 'YYYY-MM').utcOffset(0, true).startOf('month').date();
      const last_day = moment(period, 'YYYY-MM').utcOffset(0, true).endOf('month').date();

      const booking_id = +this.$store.state.editBooking;

      for (let date = first_day; date <= last_day; date++) {
        const current_date = moment(period, 'YYYY-MM')
          .utcOffset(0, true)
          .set('date', date)
          .startOf('day')
          .toISOString();

        const current_date_bookings = bookings.filter(
          (booking) =>
            booking.id !== booking_id &&
            moment(current_date).isBetween(booking.startDate, booking.endDate, 'day', '[]'),
        );

        if (!this.isDayFullyBooked(current_date, current_date_bookings)) {
          this.allowed_dates.push(current_date);
        }
      }
    },

    computeEndDateAllowedDates(period: string, bookings: Booking[]): void {
      if (this.found_end_date_booking) {
        return;
      }

      const first_day = moment(period, 'YYYY-MM').utcOffset(0, true).startOf('month').date();
      const last_day = moment(period, 'YYYY-MM').utcOffset(0, true).endOf('month').date();

      const booking_id = +(this.$store.state.editBooking as string);
      const current_booking = this.$store.state.createBooking as CreateBooking;
      const end_date = moment.max([
        moment(current_booking.startDate, CREATE_BOOKING_DATE_FORMAT),
        moment(current_booking.endDate, CREATE_BOOKING_DATE_FORMAT),
      ]);

      for (let date = first_day; date <= last_day; date++) {
        const current_date = moment(period, 'YYYY-MM')
          .utcOffset(0, true)
          .set('date', date)
          .startOf('day')
          .toISOString();
        // Ignore dates before current_date.
        if (
          moment(current_booking.startDate, CREATE_BOOKING_DATE_FORMAT).utcOffset(0, true).diff(current_date, 'days') >
          0
        ) {
          continue;
        }

        if (end_date.utcOffset(0, true).diff(current_date, 'days') > 0) {
          this.allowed_dates.push(current_date);
          continue;
        }

        const current_date_bookings = bookings.filter(
          (booking) =>
            booking.id !== booking_id &&
            moment(current_date).isBefore(booking.startDate) &&
            moment(current_date).isBetween(booking.startDate, booking.endDate, 'day', '[]'),
        );
        if (current_date_bookings.length > 0) {
          this.found_end_date_booking = true;
          const first_booking = current_date_bookings[0];
          if (end_date.isBefore(first_booking.startDate)) {
            this.allowed_dates.push(current_date);
          }
          return;
        } else {
          // No bookings, date can be booked.
          this.allowed_dates.push(current_date);
        }
      }
    },

    async setDatePickerBookingDatesColor(period: string, start_date: string, end_date: string): Promise<void> {
      await this.$nextTick();

      const dates = document.querySelectorAll('.v-date-picker-table .v-btn');
      const date_values = document.querySelectorAll('.v-date-picker-table .v-btn .v-btn__content');
      date_values.forEach((date_component, index) => {
        const date_picker_date = moment(period, 'YYYY-MM')
          .utcOffset(0, true)
          .set('date', +date_component.innerHTML);

        if (
          moment(start_date, CREATE_BOOKING_DATE_FORMAT)
            .utcOffset(0, true)
            .startOf('day')
            .isSameOrBefore(date_picker_date) &&
          moment(end_date, CREATE_BOOKING_DATE_FORMAT).utcOffset(0, true).endOf('day').isSameOrAfter(date_picker_date)
        ) {
          dates[index].classList.add('date-booked-color');
        } else {
          dates[index].classList.remove('date-booked-color');
        }
      });
    },

    async setDatePickerColor(period: string, bookings: Booking[]): Promise<void> {
      await this.$nextTick();

      const dates = document.querySelectorAll('.v-date-picker-table .v-btn');
      const date_values = document.querySelectorAll('.v-date-picker-table .v-btn .v-btn__content');
      const booking_id = +this.$store.state.editBooking;
      const booking_dates = bookings.filter((booking) => booking.id !== booking_id);
      date_values.forEach((date_component, index) => {
        // Ignore active buttons.
        if (dates[index].classList.contains('v-btn--active')) {
          return;
        }

        const date_picker_date = moment(period, 'YYYY-MM')
          .utcOffset(0, true)
          .set('date', +date_component.innerHTML)
          .startOf('day');

        const today = moment().utcOffset(0, true);
        if (today.isAfter(date_picker_date)) {
          return;
        }

        if (
          booking_dates.some(
            (date) =>
              moment(date.startDate).utcOffset(0, true).startOf('day').isSameOrBefore(date_picker_date) &&
              moment(date.endDate).utcOffset(0, true).endOf('day').isSameOrAfter(date_picker_date),
          )
        ) {
          const date_bookings = bookings.filter((booking) =>
            moment(date_picker_date).isBetween(booking.startDate, booking.endDate, 'day', '[]'),
          );
          if (this.isDayFullyBooked(date_picker_date.toISOString(), date_bookings)) {
            dates[index].classList.add('date-color');
          } else {
            dates[index].classList.add('date-half-booked-color');
          }
        }
      });
    },
  },
});
